import { CONTACT_ORGANIZATION_MEDICAL_PROVIDER_SPECIALIZATIONS } from "@/lib/data/static/contactStatics";
import type {
  Matter,
  MatterExpense,
  MatterTrustCheck,
  MatterTrustCheckConnection,
} from "@/lib/types";
import { faker, fakerEN_US } from "@faker-js/faker";
import dayjs from "dayjs";

const localizedFaker = fakerEN_US;

export const generateMockMatterKeys = () => {
  return {
    dateCreated: dayjs(faker.date.past())
      .format("YYYY-MM-DDTHH:mm:ss.SSSZ")
      .toString(),
    dateModified: dayjs(faker.date.past())
      .format("YYYY-MM-DDTHH:mm:ss.SSSZ")
      .toString(),
    matterId: faker.string.uuid(),
  };
};

export const generateMockMedicalTreatmentAppointment = (
  overrides: Record<string, unknown> = {},
) => {
  const keys = generateMockMatterKeys();
  return {
    medicalTreatmentId: faker.string.uuid(),
    connections: [],
    ...keys,
    id: faker.string.uuid(),
    date: dayjs(faker.date.future()).format("YYYY-MM-DD"),
    time: "10:00",
    type: [
      localizedFaker.helpers.arrayElement(
        CONTACT_ORGANIZATION_MEDICAL_PROVIDER_SPECIALIZATIONS,
      ),
    ],
    description: localizedFaker.lorem.sentence(),
    ...overrides,
  };
};

export const generateMockMedicalTreatmentRecord = (
  overrides: Record<string, unknown> = {},
) => {
  const keys = generateMockMatterKeys();
  return {
    medicalTreatmentId: faker.string.uuid(),
    connections: [],
    ...keys,
    name: localizedFaker.lorem.words(3),
    id: faker.string.uuid(),
    status: localizedFaker.helpers.arrayElement([
      "Requested",
      "Received",
      "",
    ] as const),
    requestDate: dayjs(faker.date.past()).format("YYYY-MM-DD"),
    ...overrides,
  };
};

export const generateMockMedicalTreatmentBill = (
  overrides: Record<string, unknown> = {},
) => {
  const keys = generateMockMatterKeys();
  const originalBill = localizedFaker.number.float({
    min: 50,
    max: 5000,
    fractionDigits: 2,
  });
  return {
    medicalTreatmentId: faker.string.uuid(),
    connections: [],
    ...keys,
    id: faker.string.uuid(),
    recordStatus: localizedFaker.helpers.arrayElement([
      "Requested",
      "Received",
      "",
    ] as const),
    recordLabel: localizedFaker.lorem.words(3),
    recordRequestDate: dayjs(faker.date.past()).format("YYYY-MM-DD"),
    originalBill: originalBill,
    ...overrides,
  };
};

export const generateMockMedicalTreatmentAppointmentConnection = (
  overrides: Record<string, unknown> = {},
) => {
  const keys = generateMockMatterKeys();
  return {
    medicalTreatmentId: faker.string.uuid(),
    ...keys,
    id: faker.string.uuid(),
    medicalTreatmentAppointmentId: faker.string.uuid(),
    medicalTreatmentRecordId: undefined as string | undefined,
    medicalTreatmentBillId: undefined as string | undefined,
    medicalTreatmentPlanId: undefined as string | undefined,
    ...overrides,
  };
};

export const generateMockMatter = (overrides: Partial<Matter> = {}): Matter => {
  return {
    id: faker.string.uuid(),
    dateCreated: dayjs(faker.date.past()).format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
    dateModified: dayjs(faker.date.past()).format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
    attorneyFee: 500,
    dateOfLoss: dayjs(faker.date.past()).format("YYYY-MM-DD"),
    incidentAddressCity: localizedFaker.location.city(),
    incidentAddressCountry: "United States",
    incidentAddressState: localizedFaker.location.state(),
    incidentAddressStreet1: localizedFaker.location.streetAddress(),
    incidentAddressStreet2: "",
    incidentAddressZip: localizedFaker.location.zipCode(),
    title: `${localizedFaker.person.lastName()} v. ${localizedFaker.company.name()}`,
    summary: localizedFaker.lorem.paragraph(),
    clientMaritalStatus: "Single",
    clientNoSocialSecurity: false,
    statusId: faker.string.uuid(),
    status: {
      id: faker.string.uuid(),
      label: "Active",
      firmId: faker.string.uuid(),
      dateCreated: dayjs(faker.date.past()).format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
      dateModified: dayjs(faker.date.past()).format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
    },
    incidentTypeId: faker.string.uuid(),
    incidentType: {
      id: faker.string.uuid(),
      label: "Personal Injury",
      firmId: faker.string.uuid(),
      dateCreated: dayjs(faker.date.past()).format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
      dateModified: dayjs(faker.date.past()).format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
    },
    medicalTreatmentAppointments: [],
    medicalTreatmentRecords: [],
    medicalTreatmentBills: [],
    medicalTreatmentAppointmentConnections: [],
    medicalTreatments: [],
    insurances: [],
    vehicles: [],
    expenses: [],
    liens: [],
    lostWages: [],
    propertyDamages: [],
    settlementDemands: [],
    settlementDemandPackageSegments: [],
    settlementOffers: [],
    settlementCounters: [],
    trustChecks: [],
    trustCheckConnections: [],
    teamConnections: [],
    files: [],
    fileReferences: [],
    ...overrides,
  } as Matter;
};

export const generateMockMatterExpense = (
  overrides: Record<string, unknown> = {},
) => {
  const keys = generateMockMatterKeys();
  return {
    ...keys,
    id: faker.string.uuid(),
    category: localizedFaker.helpers.arrayElement([
      "Filing Fees",
      "Expert Witness",
      "Court Reporter",
      "Medical Records",
      "Investigation",
      "Record Retrieval Service",
      "Travel",
      "Other",
    ]),
    cost: localizedFaker.number.float({
      min: 10,
      max: 5000,
      fractionDigits: 2,
    }),
    label: localizedFaker.lorem.words(3),
    dateIncurred: dayjs(faker.date.past()).format("YYYY-MM-DD"),
    description: localizedFaker.lorem.sentence(),
    paidByMethod: localizedFaker.helpers.maybe(() => "Credit Card", {
      probability: 0.5,
    }),
    paidStatus: localizedFaker.helpers.arrayElement([
      "Paid",
      "Unpaid",
      "Partially Paid",
    ] as const),
    paidAmount: localizedFaker.helpers.maybe(
      () =>
        localizedFaker.number.float({ min: 0, max: 1000, fractionDigits: 2 }),
      { probability: 0.7 },
    ),
    paidByMethodDetails: localizedFaker.helpers.maybe(
      () => localizedFaker.lorem.sentence(),
      { probability: 0.3 },
    ),
    refunded: localizedFaker.helpers.maybe(
      () => localizedFaker.datatype.boolean(),
      { probability: 0.2 },
    ),
    refundedAmount: localizedFaker.helpers.maybe(
      () =>
        localizedFaker.number.float({ min: 0, max: 500, fractionDigits: 2 }),
      { probability: 0.1 },
    ),
    reducedAmount: localizedFaker.helpers.maybe(
      () =>
        localizedFaker.number.float({ min: 0, max: 200, fractionDigits: 2 }),
      { probability: 0.1 },
    ),
    ...overrides,
  } as MatterExpense;
};

export const generateMockMatterTrustCheck = (
  overrides: Record<string, unknown> = {},
) => {
  const keys = generateMockMatterKeys();
  return {
    ...keys,
    id: faker.string.uuid(),
    amount: localizedFaker.number.float({
      min: 100,
      max: 50000,
      fractionDigits: 2,
    }),
    dateReceived: dayjs(faker.date.past()).format("YYYY-MM-DD"),
    details: localizedFaker.helpers.maybe(
      () => localizedFaker.lorem.sentence(),
      { probability: 0.5 },
    ),
    referenceNumber: localizedFaker.number
      .int({ min: 1000, max: 9999 })
      .toString(),
    type: localizedFaker.helpers.arrayElement(["Deposit", "Withdraw"] as const),
    ...overrides,
  } as MatterTrustCheck;
};

export const generateMockMatterTrustCheckConnection = (
  overrides: Record<string, unknown> = {},
) => {
  const keys = generateMockMatterKeys();
  return {
    ...keys,
    id: faker.string.uuid(),
    trustCheckId: faker.string.uuid(),
    settlementOfferId: undefined as string | undefined,
    settlementDemandId: undefined as string | undefined,
    settlementCounterId: undefined as string | undefined,
    expenseId: undefined as string | undefined,
    lienId: undefined as string | undefined,
    medicalTreatmentBillId: undefined as string | undefined,
    ...overrides,
  } as MatterTrustCheckConnection;
};

// Convenience function for creating a matter with medical treatment data
export const generateMockMatterWithMedicalTreatment = (
  options: {
    appointmentCount?: number;
    recordCount?: number;
    billCount?: number;
    connectionCount?: number;
    overrides?: Partial<Matter>;
  } = {},
) => {
  const {
    appointmentCount = 1,
    recordCount = 1,
    billCount = 1,
    connectionCount = 1,
    overrides = {},
  } = options;

  const appointments = Array.from({ length: appointmentCount }, () =>
    generateMockMedicalTreatmentAppointment(),
  );

  const records = Array.from({ length: recordCount }, () =>
    generateMockMedicalTreatmentRecord(),
  );

  const bills = Array.from({ length: billCount }, () =>
    generateMockMedicalTreatmentBill(),
  );

  const connections = Array.from({ length: connectionCount }, (_, i) =>
    generateMockMedicalTreatmentAppointmentConnection({
      medicalTreatmentAppointmentId: appointments[0]?.id,
      medicalTreatmentRecordId: i < records.length ? records[i].id : undefined,
      medicalTreatmentBillId: i < bills.length ? bills[i].id : undefined,
    }),
  );

  return generateMockMatter({
    medicalTreatmentAppointments: appointments,
    medicalTreatmentRecords: records,
    medicalTreatmentBills: bills,
    medicalTreatmentAppointmentConnections: connections,
    ...overrides,
  });
};
